import { message } from 'antd';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken } from 'features/auth/helpers';
import { HttpClientProps, TQueryConfig, TQueryError } from 'lib/axios/types';
import debounce from 'lodash/debounce';
import { getPath } from 'routes/router-paths';
import { startLoadingSpinner, stopLoadingSpinner } from 'utils/loadingSpinner';
import { ObjectType } from 'utils/types';

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig & TQueryConfig) => {
    const { disableSpinner } = config;
    if (!disableSpinner) {
      startLoadingSpinner();
    }
    return config;
  },
  (error: AxiosError) => {
    stopLoadingSpinner();
    return Promise.reject(error);
  }
);

const showErrorMessage = debounce((msg: string) => {
  void message.error(msg);
}, 1000);

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    stopLoadingSpinner();
    return response.data;
  },
  async (error: AxiosError) => {
    const { response, code, config } = error;
    const { manuallyHandlingErrorMsg } = config as AxiosError['config'] &
      TQueryConfig;
    stopLoadingSpinner();

    if (response?.status === 403) {
      window.location.replace(getPath('forbiddenPage'));
    }

    if (!manuallyHandlingErrorMsg && response?.data) {
      const errorMessage = (response.data as ObjectType)?.errorMessage?.messages
        ?.vi;
      const defaultMessage =
        response?.status === 408 || code === 'ECONNABORTED'
          ? 'Request timed out.'
          : 'Đã có lỗi xảy ra';
      showErrorMessage(errorMessage || defaultMessage);
    }

    return Promise.reject(manuallyHandlingErrorMsg ? response?.data : error);
  }
);

export const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: '' }) =>
  async (config: HttpClientProps & TQueryConfig) => {
    const {
      url,
      method,
      data,
      params,
      customHeader,
      manuallyHandlingErrorMsg,
      disableSpinner,
    } = config;

    const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);

    const axiosConfig = {
      url: baseUrl + url,
      method,
      headers: {
        ...(accessToken &&
          url !== '/oauth/token' && {
            Authorization: `Bearer ${accessToken}`,
          }),
        ...customHeader,
      },
      ...(params && { params }),
      ...(data && { data }),
      manuallyHandlingErrorMsg,
      disableSpinner,
    } as AxiosRequestConfig<ObjectType> & TQueryConfig;

    try {
      const result = await axios(axiosConfig);
      return {
        data: result,
      };
    } catch (err: any) {
      return {
        data: null,
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message || err.error,
          success: err.success || false,
        } as TQueryError,
      };
    }
  };
